import { useTranslation } from 'react-i18next';
import type { API } from '@/api';
import { usePrivateRoomAvailability } from '@/hooks/usePrivateRoomAvailability';

type TimeSlot =
  API['GET']['PrivateRoomAvailableTimeSlots']['response']['spaces'][number]['time_slots'][number];

interface Props {
  timeSlots: TimeSlot[];
  startHHMM: `${string}:${string}`;
  setStartHHMM: (t: `${string}:${string}` | null) => void;
  durationMinutes: number;
  setDurationMinutes: (d: number) => void;
  remainingMinutes?: number;
}

export const ReservationFormPrivateRoomSectionTimeSelect = ({
  timeSlots,
  startHHMM,
  setStartHHMM,
  durationMinutes,
  setDurationMinutes,
  remainingMinutes,
}: Props) => {
  const { t } = useTranslation();
  const [startHH, startMM] = startHHMM.split(':');

  // 利用可能性チェックのhookを使用（残り時間を考慮）
  const {
    availableHoursMap,
    allHours,
    availableMinutesSet,
    allMinutes,
    availableDurationsSet,
    allDurations,
  } = usePrivateRoomAvailability(timeSlots, startHH, startHHMM, remainingMinutes);

  return (
    <>
      <div className="booth-picker__control">
        <div className="booth-picker__control-title">
          <img
            src="/B_2_Icon_Time.png"
            alt={t('reservation.privateRoom.selectTime')}
            className="booth-picker__control--icon"
          />
          {t('reservation.privateRoom.selectTime')}
        </div>

        <div className="booth-picker__control-body">
          <div className="booth-picker__field">
            <div className="booth-picker__field-label">
              {t('reservation.privateRoom.startTime')}
            </div>

            <div className="booth-picker__field-input">
              <span className="booth-picker__field-unit">
                {t('reservation.privateRoom.hourUnit')}
              </span>
              <select
                name=""
                className="booth-picker__select booth-picker__select--hour required"
                value={startHH}
                onChange={(e) => {
                  const newHour = e.target.value;
                  // 時間が変更されたら、その時間の最初の利用可能な分に設定
                  const firstAvailableMinute = timeSlots.find(
                    (slot) =>
                      slot.time.startsWith(`${newHour}:`) &&
                      slot.available &&
                      slot.displayable,
                  );
                  if (firstAvailableMinute) {
                    setStartHHMM(
                      firstAvailableMinute.time as `${string}:${string}`,
                    );
                  }
                  setDurationMinutes(0);
                }}
              >
                <option value="_" disabled>
                  --
                </option>
                {allHours.map((hour) => {
                  const isDisabled = !availableHoursMap.get(hour);
                  return (
                    <option
                      key={hour}
                      value={hour}
                      disabled={isDisabled}
                      className={isDisabled ? 'disabled-option-item' : ''}
                    >
                      {hour}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="booth-picker__field-input">
              <span className="booth-picker__field-unit">
                {t('reservation.privateRoom.minuteUnit')}
              </span>
              <select
                name=""
                className="booth-picker__select booth-picker__select--minute required"
                value={startMM}
                onChange={(e) => {
                  const newMinute = e.target.value;
                  setStartHHMM(
                    `${startHH}:${newMinute}` as `${string}:${string}`,
                  );
                  setDurationMinutes(0);
                }}
              >
                <option value="_" disabled>
                  --
                </option>
                {allMinutes.map((minute) => {
                  const isDisabled = !availableMinutesSet.has(minute);
                  return (
                    <option
                      key={minute}
                      value={minute}
                      disabled={isDisabled}
                      className={isDisabled ? 'disabled-option-item' : ''}
                    >
                      {minute}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="booth-picker__field">
            <div className="booth-picker__field-label">
              {t('reservation.privateRoom.duration')}
            </div>
            <div className="booth-picker__field-input booth-picker__field-input--time">
              <select
                name=""
                className="booth-picker__select booth-picker__select--duration"
                required
                value={durationMinutes}
                onChange={(e) => {
                  setDurationMinutes(Number(e.target.value));
                }}
              >
                <option value={0} disabled>
                  --
                </option>
                {allDurations.map((duration) => {
                  const isDisabled = !availableDurationsSet.has(duration);
                  return (
                    <option
                      key={duration}
                      value={duration}
                      disabled={isDisabled}
                      className={isDisabled ? 'disabled-option-item' : ''}
                    >
                      {duration.toString().padStart(2, '0')}
                    </option>
                  );
                })}
              </select>
              <span className="booth-picker__field-unit booth-picker__field-unit--duration">
                {t('reservation.privateRoom.minutesDuration')}
              </span>
            </div>
          </div>
          <div className="booth-picker__field--note">
            {t('reservation.privateRoom.usageNote')}
          </div>
        </div>
      </div>
    </>
  );
};
